import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import { ReviewNoteEditor } from '@/components/admin/sessions/review/review-note-editor';
import { authorLabel, reviewMoment, validationsOf } from '@/lib/review-labels';
import type { ReviewValidation } from '@matter_extractor/shared';
import { Button } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * The validations of a case (relecture.md §6): all of them, the most recent
 * first, then the two buttons.
 *
 * A validation that predates the last modification of its case stays readable,
 * struck through and marked « avant modification » - `current` is derived by
 * the server (`currentValidation`), never computed here. « Je valide » and « Je
 * ne valide pas » open a small field for a facultative note, and the button
 * that confirms says what it is: the verdict is posed on the filing ALREADY in
 * the review, by a colleague or by oneself another time, never on the machine's.
 */

export interface ReviewValidationsProps {
  readonly objectiveId: string;
}

const VERDICT_LABEL = { valid: 'Validée', invalid: 'Non validée' } as const;

function ValidationRow({ validation }: { readonly validation: ReviewValidation }) {
  const stale = !validation.current;
  const tone = validation.verdict === 'valid' ? 'text-vert-deep' : 'text-rouge-deep';
  return (
    <div
      data-testid={REVIEW_TEST_IDS.validation}
      data-verdict={validation.verdict}
      data-current={validation.current}
      className={`flex flex-col gap-0.5 text-caption ${stale ? 'text-gray-500' : ''}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <span className={`font-bold ${stale ? 'text-gray-500 line-through' : tone}`}>
          {VERDICT_LABEL[validation.verdict]}
        </span>
        <span>
          par {authorLabel(validation.by)} · {reviewMoment(validation.at)}
        </span>
        {validation.via === 'claude' ? (
          <span className="inline-flex items-center rounded-sm bg-bleu-ciel-tint px-1.5 font-semibold text-bleu-ciel-deep text-micro">
            {validation.dapres === null
              ? 'via Claude'
              : `via Claude · d’après ${validation.dapres}`}
          </span>
        ) : null}
        {stale ? (
          <span className="rounded-sm bg-gray-100 px-1.5 font-semibold text-gray-600 text-micro">
            avant modification
          </span>
        ) : null}
      </div>
      {validation.note === '' ? null : <span>{validation.note}</span>}
    </div>
  );
}

export function ReviewValidations({ objectiveId }: ReviewValidationsProps) {
  const { review, apply, canWrite, busy } = useReviewApi();
  const [verdict, setVerdict] = useState<'valid' | 'invalid' | null>(null);
  const rows = validationsOf(review.validations, objectiveId);

  const save = async (note: string): Promise<void> => {
    if (verdict === null) return;
    const applied = await apply([{ op: 'validate-case', objectiveId, verdict, note: note.trim() }]);
    if (applied) setVerdict(null);
  };

  return (
    <div className="flex flex-col gap-1.5 border-gray-200 border-t border-dashed pt-2">
      {rows.map((validation) => (
        <ValidationRow key={validation.id} validation={validation} />
      ))}
      {!canWrite ? null : verdict === null ? (
        <div className="flex flex-wrap gap-1.5">
          <Button
            type="button"
            size="xs"
            compact={true}
            variant="accent"
            icon="check"
            data-testid={REVIEW_TEST_IDS.validate}
            data-verdict="valid"
            disabled={busy}
            onClick={() => setVerdict('valid')}
          >
            Je valide
          </Button>
          <Button
            type="button"
            size="xs"
            compact={true}
            variant="accent"
            icon="close"
            data-testid={REVIEW_TEST_IDS.validate}
            data-verdict="invalid"
            disabled={busy}
            onClick={() => setVerdict('invalid')}
          >
            Je ne valide pas
          </Button>
        </div>
      ) : (
        <ReviewNoteEditor
          label="Note de la validation"
          placeholder="Pourquoi (facultatif)"
          confirm={verdict === 'valid' ? 'Je valide' : 'Je ne valide pas'}
          tone={verdict === 'valid' ? 'ok' : 'ko'}
          busy={busy}
          onSave={(text) => void save(text)}
          onCancel={() => setVerdict(null)}
        />
      )}
    </div>
  );
}
